"use client";

import { Button } from "@ui-d2c/components/ui/button";
import { MagnifyingGlassIcon, XMarkIcon } from "@ui-d2c/components/ui/icon";
import { ScrollArea } from "@ui-d2c/components/ui/scroll-area";
import type { ProductSearchBar } from "@ui-d2c/types";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchSuggestionsFromApi,
  getBaseUrl,
} from "@/utils/storefront-search.utils";

// Search configuration constants
const MIN_SEARCH_LENGTH = 3; // Minimum characters required before showing suggestions
const SEARCH_DEBOUNCE_MS = 300; // Debounce delay for search input in milliseconds

const debounce = <T extends unknown[]>(
  func: (...args: T) => void,
  delay: number
) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: T) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export function AnimatedSearchField() {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<ProductSearchBar[]>([]);
  const baseUrl = getBaseUrl();

  const fetchSuggestions = useCallback(
    async (value: string) => {
      if (value.length >= MIN_SEARCH_LENGTH) {
        setLoading(true);
        try {
          const results = await fetchSuggestionsFromApi(baseUrl || "", value);
          setSuggestions(results);
        } catch (_error) {
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
      }
    },
    [baseUrl]
  );

  const fetchSuggestionsRef = useRef(fetchSuggestions);
  fetchSuggestionsRef.current = fetchSuggestions;

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      void fetchSuggestionsRef.current(value);
    }, SEARCH_DEBOUNCE_MS),
    []
  );

  const handleInputChange = (value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    setShowSuggestions(query.length >= MIN_SEARCH_LENGTH);
  }, [query]);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
        break;
      case "Enter":
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          setQuery(suggestions[selectedIndex]?.name || "");
          setShowSuggestions(false);
          setSelectedIndex(-1);
          window.location.href = `${baseUrl}${suggestions[selectedIndex]?.url}`;
        } else {
          window.location.href = `${baseUrl}search?${new URLSearchParams({ text: query }).toString()}`;
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        // Other keys don't affect suggestion navigation
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`absolute right-20 flex h-12 justify-end transition-all duration-300 lg:right-10 2xl:right-10 ${isExpanded ? "w-[calc(100%-95px)] border border-neutral-200 md:w-[calc(100%-284px)] lg:w-[calc(100%-304px)] xl:w-[calc(100%-330px)]" : "w-12"}`}
      ref={containerRef}
    >
      <div
        className={`relative mr-12 flex overflow-hidden bg-background font-normal ${isExpanded ? "w-full" : "w-0"}`}
      >
        <MagnifyingGlassIcon
          className="absolute top-4 left-3"
          size="1x"
          title="MagnifyingGlass icon"
        />
        <input
          className="h-12 w-full rounded-none border-0 pl-12 focus:outline-none"
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowSuggestions(query.length >= MIN_SEARCH_LENGTH)}
          onKeyDown={handleKeyDown}
          placeholder="I'm looking for..."
          ref={inputRef}
          value={query}
        />
      </div>
      {showSuggestions && isExpanded && (
        <div
          className="absolute top-11 z-10 w-full rounded-b border bg-background shadow-lg"
          ref={suggestionsRef}
        >
          <ScrollArea className="h-fit p-2">
            {loading && (
              <div className="p-2 text-muted-foreground text-sm">
                Searching...
              </div>
            )}
            {!loading && suggestions.length > 0 && (
              <ul className="">
                {suggestions.map((item, index) => (
                  <li
                    className={`cursor-pointer border-neutral-200 p-4 text-sm ${
                      index === selectedIndex ? "bg-muted" : ""
                    }
                      ${index === suggestions.length - 1 ? "rounded-b" : "border-b"}
                      `}
                    key={item.code}
                    onClick={() => {
                      setQuery(item.name);
                      setShowSuggestions(false);
                      window.location.href = `${baseUrl}${item.url}`;
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
            {!loading && suggestions.length === 0 && (
              <div className="p-2 text-muted-foreground text-sm">
                No results found.
              </div>
            )}
          </ScrollArea>
        </div>
      )}
      <Button
        aria-label={isExpanded ? "Close search" : "Open search"}
        className="absolute right-0 h-12 w-12 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        variant="ghost"
      >
        {isExpanded ? (
          <XMarkIcon
            className={"p-4 text-neutral-600"}
            size="1x"
            title="XMark icon"
          />
        ) : (
          <MagnifyingGlassIcon
            className={"p-4 text-neutral-600"}
            onClick={() => setIsExpanded(!isExpanded)}
            size="1x"
            title="MagnifyingGlass icon"
          />
        )}
      </Button>
    </div>
  );
}
